/**
 * HFDS — Headzflex Design System
 * Component: HF-013 Site Footer
 * Version: 1.2.0
 *
 * Shopify customer forms reload the page after submission. In this
 * storefront, the successful return URL contains the form ID as its hash,
 * but it does not retain a success query parameter that Liquid can always
 * use after redirecting to the homepage.
 *
 * This script:
 * - Stores a short-lived marker only when a valid newsletter form submits.
 * - Confirms the returning URL hash matches that exact form.
 * - Reveals the accessible success message.
 * - Clears the marker so a later visit does not show a false confirmation.
 */

(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-hfds-footer]";
  const FORM_ID_PREFIX = "HFDSFooterForm-";
  const SUCCESS_SELECTOR =
    "[data-hfds-newsletter-success]";
  const STORAGE_PREFIX =
    "hfds:newsletter:pending:";
  const MAX_AGE_MS = 5 * 60 * 1000;

  function getStorageKey(formId) {
    return `${STORAGE_PREFIX}${formId}`;
  }

  function readMarker(formId) {
    try {
      const raw = window.sessionStorage.getItem(
        getStorageKey(formId)
      );

      if (!raw) {
        return null;
      }

      const marker = JSON.parse(raw);
      const createdAt = Number(marker?.createdAt);

      if (
        !Number.isFinite(createdAt) ||
        Date.now() - createdAt > MAX_AGE_MS
      ) {
        window.sessionStorage.removeItem(
          getStorageKey(formId)
        );

        return null;
      }

      return marker;
    } catch (error) {
      return null;
    }
  }

  function writeMarker(formId, email) {
    try {
      window.sessionStorage.setItem(
        getStorageKey(formId),
        JSON.stringify({
          createdAt: Date.now(),
          email: String(email || "").trim()
        })
      );
    } catch (error) {
      // The native Shopify form remains fully functional.
    }
  }

  function clearMarker(formId) {
    try {
      window.sessionStorage.removeItem(
        getStorageKey(formId)
      );
    } catch (error) {
      // No action required.
    }
  }

  function getLocationHash() {
    try {
      return decodeURIComponent(
        window.location.hash.slice(1)
      );
    } catch (error) {
      return window.location.hash.slice(1);
    }
  }

  function revealSuccess({
    form,
    input,
    message
  }) {
    message.hidden = false;

    if (input instanceof HTMLInputElement) {
      input.value = "";
    }

    message.focus({
      preventScroll: true
    });

    form.dataset.hfdsSubmitted =
      "true";
  }

  function initializeFooter(footer) {
    if (
      !(footer instanceof HTMLElement) ||
      footer.dataset.hfdsFooterInitialized ===
        "true"
    ) {
      return;
    }

    const configuredFormId =
      footer.dataset.hfdsFooterFormId || "";

    const form =
      configuredFormId
        ? document.getElementById(
            configuredFormId
          )
        : footer.querySelector(
            `form[id^="${FORM_ID_PREFIX}"]`
          );

    const input = form?.querySelector(
      'input[name="contact[email]"]'
    );

    const message = footer.querySelector(
      SUCCESS_SELECTOR
    );

    if (
      !(form instanceof HTMLFormElement) ||
      !(message instanceof HTMLElement)
    ) {
      return;
    }

    const formId = form.id;

    form.addEventListener(
      "submit",
      () => {
        if (!form.checkValidity()) {
          return;
        }

        writeMarker(
          formId,
          input?.value
        );
      }
    );

    const hasServerError = Boolean(
      form.querySelector(
        ".hfds-footer__form-message.is-error"
      )
    );

    const serverSuccess =
      message.hidden === false;

    const returnedToForm =
      getLocationHash() === formId;

    const marker =
      readMarker(formId);

    if (hasServerError) {
      clearMarker(formId);
    } else if (
      serverSuccess ||
      (returnedToForm && marker)
    ) {
      revealSuccess({
        form,
        input,
        message
      });

      clearMarker(formId);

      /*
       * Keep the confirmation visible while cleaning the URL. This avoids
       * showing the same success state again after a manual refresh.
       */
      window.setTimeout(() => {
        if (
          window.location.hash ===
          `#${formId}`
        ) {
          window.history.replaceState(
            null,
            "",
            `${window.location.pathname}${window.location.search}`
          );
        }
      }, 250);
    }

    footer.dataset.hfdsFooterInitialized =
      "true";
  }

  function initialize(context = document) {
    context
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(initializeFooter);
  }

  if (
    document.readyState === "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      () => initialize(document),
      { once: true }
    );
  } else {
    initialize(document);
  }

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initialize(event.target);
    }
  );

  window.HFDSFooter = Object.freeze({
    version: "1.2.0",
    initialize
  });
})();
